import React, { useCallback, useEffect, useState } from 'react';
import { Copy, MessageCircle, Ticket, TrendingUp } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * ReferralCouponCard — shows the user's 6-digit Anant coupon code plus a
 * WhatsApp-status ready share text. Every successful register-with-coupon
 * credits +10 AT to both sides (referrer & new user).
 */
export default function ReferralCouponCard() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/auth/referral-coupon/me'); setData(r.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { code, bonus_at_per_signup, total_uses, total_bonus_earned, recent_uses = [] } = data;
  const registerUrl = `${window.location.origin}/register?coupon=${code}`;
  const shareText =
    `🎁 *Anant Web Portal* pe FREE signup karo aur mera coupon apply karke ${bonus_at_per_signup} AT bonus lo!\n\n` +
    `👉 USE CODE: *${code}*\n\n` +
    `Direct link: ${registerUrl}\n\n` +
    `Trading + Tarot + Vyapar CRM + Safety QR — sab kuch ek jagah. Join karo aur Affiliate Network webchain se kamai shuru karo.`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const statusUrl = `whatsapp://send?text=${encodeURIComponent(shareText)}`; // opens WA status via native handler

  return (
    <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-pink-500/10 p-4 md:p-5 backdrop-blur-sm" data-testid="referral-coupon-card">
      <div className="flex items-center gap-2 mb-2">
        <Ticket className="w-5 h-5 text-amber-300" />
        <h3 className="font-extrabold text-lg text-amber-100">🎁 Aapka Anant Coupon Code</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest font-bold bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">+{bonus_at_per_signup} AT bonus</span>
      </div>
      <p className="text-xs text-slate-300 mb-3">WhatsApp status me daal do — jab bhi koi is code se signup karega, dono ko instant <b className="text-emerald-300">{bonus_at_per_signup} AT</b> milega. Har use pe aapki Affiliate Network webchain bhi grow hogi.</p>

      {/* Big code display */}
      <div className="rounded-xl bg-slate-900/60 border-2 border-dashed border-amber-400/50 p-4 text-center mb-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Your Unique Code</div>
        <div className="text-4xl md:text-5xl font-black font-mono tracking-widest text-amber-200 mt-1 mb-1" data-testid="coupon-code">
          {code}
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText(code); toast.success(`Code ${code} copied`); }}
          data-testid="coupon-copy-code"
          className="text-xs text-amber-300 hover:text-amber-100 inline-flex items-center gap-1 font-bold"
        >
          <Copy className="w-3.5 h-3.5" /> Copy Code
        </button>
      </div>

      {/* Share buttons */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <a href={waUrl} target="_blank" rel="noreferrer" data-testid="coupon-share-wa"
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs">
          <MessageCircle className="w-3.5 h-3.5" /> Share on WhatsApp
        </a>
        <button
          onClick={() => { navigator.clipboard.writeText(shareText); toast.success('Status text copied — WhatsApp status me paste karo'); }}
          data-testid="coupon-copy-status"
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-bold"
        >
          <Copy className="w-3.5 h-3.5" /> Copy Status Text
        </button>
      </div>
      <a href={statusUrl} className="hidden" aria-hidden>native</a>

      {/* Stats + recent uses */}
      <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <div className="flex items-center gap-1.5 text-slate-300">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
            <span>Signups via your coupon</span>
          </div>
          <div className="font-bold text-emerald-300" data-testid="coupon-total-uses">{total_uses} · <span className="font-mono">+{total_bonus_earned} AT earned</span></div>
        </div>
        {recent_uses.length > 0 && (
          <div className="space-y-1 mt-2">
            {recent_uses.map((u, i) => (
              <div key={i} className="text-[11px] flex items-center justify-between bg-white/5 rounded px-2 py-1" data-testid={`coupon-use-${i}`}>
                <span>@{u.new_user_username}</span>
                <span className="text-emerald-300 font-bold">+{u.bonus_at} AT</span>
              </div>
            ))}
          </div>
        )}
        {recent_uses.length === 0 && <div className="text-[11px] text-slate-500 italic">Abhi tak koi use nahi — WhatsApp status me daal do 👆</div>}
      </div>
    </div>
  );
}
